/**
 * RouteAdmin
 * -----------------------------------------------------------------------------
 * This component acts as a dynamic route-based wrapper for admin forms.
 * It selects and renders the appropriate admin component (create, duplicate, view, etc.)
 * based on the current URL path, and wraps it in a Formik provider for consistent
 * form handling and submission logic.
 *
 * USAGE:
 * - Pass a `components` object with keys for each admin action (e.g., create, duplicate, view).
 *   Each key should map to a React component for that action.
 * - Optionally provide `initialValues` for the form. The onSubmit handler can be provided later via Formik context.
 * - The component will automatically render the correct child component based on the route.
 *
 * Example:
 * <RouteAdmin
 *   components={{
 *     create: MyCreateComponent,
 *     duplicate: MyDuplicateComponent,
 *     view: MyViewComponent
 *   }}
 *   initialValues={{ name: '', description: '' }}
 * />
 *
 * EXTENDING:
 * - To add new admin actions, add a new key/component to the `components` prop.
 * - The fallback is the should be the 404 page, this behavior can be added later as needed.
 * - The form state and submission are managed by Formik context.
 *
 * DEV INTENT:
 * - This component is designed to support dynamic initial values, enabling both create and edit modes.
 * - It allows for future extension to support additional admin actions (e.g., delete, archive) by simply adding new keys to the `components` prop.
 * - The intent is to centralize route-based admin logic and keep form state management consistent and maintainable.
 */

import React, { FC, useEffect } from 'react';
import { Formik } from 'formik';
import formikData from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import workOrderFormikData from 'screens/Applications&WorkOrders/WorkOrders/Constants/workOrderFormikData';
import { useHistory, useLocation } from 'react-router-dom';
import CreateWorkOrder from 'screens/Applications&WorkOrders/WorkOrders/Components/CreateWorkOrder';
import EditWorkOrder from 'screens/Applications&WorkOrders/WorkOrders/Components/EditWorkOrder';
import CreateApplicationFromWorkOrder from 'screens/Applications&WorkOrders/Applications/Components/CreateApplicationFromWorkOrder';
import CreateOperationFromInvoice from 'screens/Applications&WorkOrders/Components/CreateOperationFromInvoice';
import CreateOperationFromRecommendation from 'screens/Applications&WorkOrders/Components/CreateOperationFromRecommendation';
import CreateOperationFromPlan from 'screens/Applications&WorkOrders/Plans/Components/CreateOperationFromPlan';
import { OperationDetails } from 'screens/Applications&WorkOrders/Components/types';
import { OperationType } from 'components/RecordsListView/constants';
import EditApplication from '../Applications/Components/EditApplication';
import CreateApplication from '../Applications/Components/CreateApplication';
import DuplicateOperation from '../Components/DuplicateOperation';
import { AgOperationUtilityProvider } from '../StepsLayout/Context/AgOperationUtilityProvider';

export enum Flow {
  CREATE,
  DUPLICATE,
  FROM_WORK_ORDER_TO_APPLICATION,
  FROM_INVOICE_TO_OPERATION,
  FROM_RECOMMENDATION_TO_OPERATION,
  FROM_PLAN_TO_OPERATION,
  VIEW,
  EDIT
}

// Determines if the flow has operation data already loaded into it (edit, duplicate, and all the conversion flows)
export const isLoadedFlow = (flow: Flow = Flow.CREATE) => {
  return [
    Flow.EDIT,
    Flow.DUPLICATE,
    Flow.FROM_WORK_ORDER_TO_APPLICATION,
    Flow.FROM_INVOICE_TO_OPERATION,
    Flow.FROM_RECOMMENDATION_TO_OPERATION,
    Flow.FROM_PLAN_TO_OPERATION
  ].includes(flow);
};

export type RoutePathState = {
  operation?: OperationDetails;
  recommendation?: any; // RecommendationVO from location.state
  invoice?: any; // InvoiceVO from location.state
  targetOperation?: OperationType;
  plan?: { id: string; [key: string]: any };
  flow?: Flow;
  returnPath?: string;
};

interface RouteAdminProps {
  flow: Flow;
  operationType: OperationType;
}

const components = {
  [Flow.CREATE]: {
    [OperationType.APPLICATION]: CreateApplication,
    [OperationType.WORK_ORDER]: CreateWorkOrder
  },
  [Flow.EDIT]: {
    [OperationType.APPLICATION]: EditApplication,
    [OperationType.WORK_ORDER]: EditWorkOrder
  },
  [Flow.DUPLICATE]: {
    [OperationType.APPLICATION]: DuplicateOperation,
    [OperationType.WORK_ORDER]: DuplicateOperation
  },
  [Flow.FROM_WORK_ORDER_TO_APPLICATION]: {
    [OperationType.APPLICATION]: CreateApplicationFromWorkOrder, // Flow from work order to application
    [OperationType.WORK_ORDER]: () => <></> // Flow from work order to work order is not supported; render empty fragment intentionally
  },
  [Flow.FROM_INVOICE_TO_OPERATION]: {
    [OperationType.APPLICATION]: CreateOperationFromInvoice,
    [OperationType.WORK_ORDER]: CreateOperationFromInvoice
  },
  [Flow.FROM_RECOMMENDATION_TO_OPERATION]: {
    [OperationType.APPLICATION]: CreateOperationFromRecommendation,
    [OperationType.WORK_ORDER]: CreateOperationFromRecommendation
  },
  [Flow.FROM_PLAN_TO_OPERATION]: {
    [OperationType.APPLICATION]: CreateOperationFromPlan,
    [OperationType.WORK_ORDER]: CreateOperationFromPlan
  },
  [Flow.VIEW]: {
    [OperationType.APPLICATION]: () => <div>ViewApplicationComponent</div>, // Placeholder for view component
    [OperationType.WORK_ORDER]: () => <div>ViewWorkOrderComponent</div> // Placeholder for view component
  }
};

const formikProps = {
  [Flow.CREATE]: {
    [OperationType.APPLICATION]: formikData,
    [OperationType.WORK_ORDER]: workOrderFormikData
  },
  [Flow.EDIT]: {
    [OperationType.APPLICATION]: formikData,
    [OperationType.WORK_ORDER]: workOrderFormikData
  },
  [Flow.DUPLICATE]: {
    [OperationType.APPLICATION]: formikData,
    [OperationType.WORK_ORDER]: workOrderFormikData
  },
  [Flow.FROM_WORK_ORDER_TO_APPLICATION]: {
    [OperationType.APPLICATION]: formikData,
    [OperationType.WORK_ORDER]: workOrderFormikData
  },
  [Flow.FROM_INVOICE_TO_OPERATION]: {
    [OperationType.APPLICATION]: formikData,
    [OperationType.WORK_ORDER]: workOrderFormikData
  },
  [Flow.FROM_RECOMMENDATION_TO_OPERATION]: {
    [OperationType.APPLICATION]: formikData,
    [OperationType.WORK_ORDER]: workOrderFormikData
  },
  [Flow.FROM_PLAN_TO_OPERATION]: {
    [OperationType.APPLICATION]: formikData,
    [OperationType.WORK_ORDER]: workOrderFormikData
  },
  [Flow.VIEW]: {
    [OperationType.APPLICATION]: formikData,
    [OperationType.WORK_ORDER]: workOrderFormikData
  }
};

const RouteAdmin: FC<RouteAdminProps> = ({ flow, operationType }) => {
  const history = useHistory();
  const { state } = useLocation<RoutePathState | undefined>();

  const currentFlow = state?.flow || flow;
  const returnPath = state?.returnPath;

  useEffect(() => {
    if (state?.flow === currentFlow) return;

    history.replace({
      state: {
        ...state,
        flow: currentFlow
      }
    });
  }, [history, state, currentFlow]);

  // Invoices reuse the shared UI shell but never route through RouteAdmin, so
  // OperationType.INVOICE is never a valid key for the create/edit maps below.
  // Narrow to the routable operation types to keep the maps unchanged (AD-2).
  const routableOperationType = operationType as
    | OperationType.APPLICATION
    | OperationType.WORK_ORDER;

  const RenderedComponent: React.FC =
    components[currentFlow][routableOperationType];

  return (
    <Formik
      initialValues={
        formikProps[currentFlow][routableOperationType].initialValues
      }
      validationSchema={
        formikProps[currentFlow][routableOperationType].validationSchema
      }
      onSubmit={() => {}}
    >
      {() => (
        <AgOperationUtilityProvider returnPath={returnPath}>
          <RenderedComponent />
        </AgOperationUtilityProvider>
      )}
    </Formik>
  );
};

export default RouteAdmin;
